import Parse from "parse";
import * as ReactSentry from "@sentry/react";
import environment, { electronOnly } from "src/utils/environment";
import Presence from "src/constants/presence";
import { signInAsAnonymousUser } from "src/modules/auth/ajax/cloud";
import { anonymousSignInSuccess } from "src/modules/auth/duck/actions";
import {
  getUser,
  getXmppCredentials,
  getDeviceName
} from "src/modules/auth/duck/selectors";
import { registerXmppDevice } from "src/modules/device/ajax/cloud";
import { wasInDashboardBefore, getLastRole } from "src/duck/selectors";
import * as actions from "src/duck/actions";
import { getXmppServer } from "src/modules/activity-log/ajax/cloud";
import { updateStorageId } from "src/modules/activity-log/duck/actions";
import { hydrateStore as hydrateFirebaseStore } from "src/modules/firebase/duck/actions";
import { instantiateActivityLogUploader } from "src/modules/activity-log/lib/ActivityLogUploader";
import { initRemoteConfig } from "src/modules/firebase";
import { logInWithWebCredentials } from "src/browser/browserCredentials";
import updateInstallationInfo from "./updateInstallationInfo";
import updateWindowSize from "src/scenes/Dashboard/updateWindowSize";
import FreemiumVideo from "src/modules/parent-station/FreemiumVideo";

const log = window.require("electron-log");
const { isBrowser, allowAnonymousInBrowser } = environment;
const Sentry = isBrowser ? ReactSentry : window.require("@sentry/electron");

export default function startApp({
  navigate,
  messenger,
  synchronizer,
  handleAppReset,
  activityService
}) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      let user = getUser(state);
      let xmppCredentials = getXmppCredentials(state);

      if (!xmppCredentials || !user) {
        const deviceName = getDeviceName(state);
        xmppCredentials = await registerXmppDevice(deviceName);

        await electronOnly(
          async () => {
            user = await signInAsAnonymousUser(xmppCredentials.xmppLogin);
            dispatch(anonymousSignInSuccess({ user, xmppCredentials }));
            Sentry?.configureScope((scope) => {
              scope.setUser({ id: user.objectId });
            });
          },
          { disable: allowAnonymousInBrowser }
        );
        if (isBrowser && !allowAnonymousInBrowser) {
          await logInWithWebCredentials({ synchronizer, xmppCredentials });
        }

        await synchronizer.synchronizeLullabies();
      } else {
        Sentry?.configureScope((scope) => {
          scope.setUser({
            id: user?.objectId,
            email: user?.email,
            optionalEmail: user?.optionalEmail
          });
        });
        await synchronizer.synchronize();
      }

      await initRemoteConfig();
      FreemiumVideo.fetchConfig();
      await messenger.initiate({ xmppCredentials });
      const pairedDevices = await messenger.fetchRoster();
      await synchronizer.synchronizeDevices();
      messenger.setPresence(Presence.NOT_CHOSEN_YET);
      activityService.initialize(messenger);

      if (
        pairedDevices.length > 0 ||
        wasInDashboardBefore(getState()) ||
        isBrowser
      ) {
        const lastRole = getLastRole(getState());
        if (lastRole) updateWindowSize();
        if (lastRole === "baby") navigate("/baby-station");
        else if (lastRole === "parent") navigate("/parent-station");
        else navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }

      dispatch(actions.appStartSuccess());
      dispatch(hydrateFirebaseStore());
      updateInstallationInfo();
      const storageId = await getXmppServer();
      if (storageId) dispatch(updateStorageId(storageId));
      electronOnly(() => {
        instantiateActivityLogUploader();
      });
    } catch (error) {
      try {
        await messenger.stop();
      } catch (err) {
        log.error(err);
      }
      log.error("startApp failure", error);
      if (
        error?.code === Parse.Error.INVALID_SESSION_TOKEN ||
        error?.message === "Device not found" ||
        error?.code === Parse.Error.SESSION_MISSING
      ) {
        handleAppReset();
        return;
      }
      dispatch(actions.appFailure());
    }
  };
}
